import { ProviderToken, Type } from '@angular/core';
import { Observable, switchMap } from 'rxjs';

export type MaybeNull<T> = T | null;
export type MaybeUndefined<T> = T | undefined;

export type ProviderBy<T> =
  | { useValue: T }
  | { useClass: Type<T> }
  | { useExisting: Type<T> }
  | { useFactory: () => T };

export type Repeat<Len extends number, Value, Acc extends Array<unknown> = []> = Acc['length'] extends Len
  ? Acc
  : Repeat<Len, Value, [...Acc, Value]>;

export function assertNonNullish<TValue>(value: TValue, message?: string): asserts value is NonNullable<TValue> {
  if (value === null || value === undefined) {
    throw new Error(message ?? `value is ${value === undefined ? 'undefined' : 'null'}`);
  }
}

export function isSatisfyUnion<T extends string>(value: string, allowedValues: ReadonlyArray<T>): value is T {
  return allowedValues.includes(value as T);
}

// Provider is not generic therefore it is possible to provide anything to any.
// Generic createProvider<T>() function checks if the value type is in sync with injection provider type.
export const createProvider = <T>(token: ProviderToken<T>, object: ProviderBy<T>) => ({ provide: token, ...object });

export function toVoid<T>(source: Observable<T>) {
  return source.pipe(switchMap(() => new Observable<void>((observer) => observer.next())));
}
